import { create } from 'zustand';

import { Comment } from '@/types/comment';

export const EMPTY_COMMENTS: Comment[] = [];

type CommentsScopeState = {
  comments: Comment[];
  loading: boolean;
  error: string | null;
};

const createEmptyScope = (): CommentsScopeState => ({
  comments: [],
  loading: false,
  error: null,
});

interface CommentsState {
  scopes: Record<string, CommentsScopeState>;

  setComments: (scopeKey: string, comments: Comment[]) => void;
  appendComments: (scopeKey: string, comments: Comment[]) => void;
  addComment: (scopeKey: string, comment: Comment) => void;
  addReply: (scopeKey: string, parentCommentId: number) => void;
  updateComment: (scopeKey: string, id: number, content: string) => void;
  deleteComment: (scopeKey: string, id: number) => void;
  setLoading: (scopeKey: string, loading: boolean) => void;
  setError: (scopeKey: string, error: string | null) => void;
}

function patchScope(
  scopes: Record<string, CommentsScopeState>,
  scopeKey: string,
  patch: (current: CommentsScopeState) => CommentsScopeState,
): Record<string, CommentsScopeState> {
  const current = scopes[scopeKey] ?? createEmptyScope();
  return {
    ...scopes,
    [scopeKey]: patch(current),
  };
}

export const useCommentsStore = create<CommentsState>((set) => ({
  scopes: {},

  setComments: (scopeKey, comments) => {
    set((state) => ({
      scopes: patchScope(state.scopes, scopeKey, (current) => ({
        ...current,
        comments,
      })),
    }));
  },

  appendComments: (scopeKey, newComments) => {
    set((state) => ({
      scopes: patchScope(state.scopes, scopeKey, (current) => {
        const existingIds = new Set(current.comments.map((comment) => comment.id));
        const uniqueComments = newComments.filter((comment) => !existingIds.has(comment.id));
        if (uniqueComments.length === 0) {
          return current;
        }
        return {
          ...current,
          comments: [...current.comments, ...uniqueComments],
        };
      }),
    }));
  },

  addComment: (scopeKey, comment) => {
    set((state) => ({
      scopes: patchScope(state.scopes, scopeKey, (current) => ({
        ...current,
        comments: [comment, ...current.comments],
      })),
    }));
  },

  addReply: (scopeKey, parentCommentId) => {
    set((state) => ({
      scopes: patchScope(state.scopes, scopeKey, (current) => ({
        ...current,
        comments: current.comments.map((comment) =>
          comment.id === parentCommentId
            ? {
                ...comment,
                childrenCount: {
                  ...comment.childrenCount,
                  ...(comment?.childrenCount?.totalDocs
                    ? { totalDocs: comment.childrenCount.totalDocs + 1 }
                    : { totalDocs: 1 }),
                },
              }
            : comment,
        ),
      })),
    }));
  },

  updateComment: (scopeKey, id, content) => {
    const editedAt = new Date().toISOString();
    set((state) => ({
      scopes: patchScope(state.scopes, scopeKey, (current) => ({
        ...current,
        comments: current.comments.map((comment) =>
          comment.id === id
            ? {
                ...comment,
                content,
                isEdited: true,
                editedAt,
                updatedAt: editedAt,
              }
            : comment,
        ),
      })),
    }));
  },

  deleteComment: (scopeKey, id) => {
    set((state) => ({
      scopes: patchScope(state.scopes, scopeKey, (current) => ({
        ...current,
        comments: current.comments.filter((comment) => comment.id !== id),
      })),
    }));
  },

  setLoading: (scopeKey, loading) => {
    set((state) => ({
      scopes: patchScope(state.scopes, scopeKey, (current) => ({
        ...current,
        loading,
      })),
    }));
  },

  setError: (scopeKey, error) => {
    set((state) => ({
      scopes: patchScope(state.scopes, scopeKey, (current) => ({
        ...current,
        error,
      })),
    }));
  },
}));
